import React, { useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import {
    Box,
    Paper,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    Toolbar,
    Typography,
    Tooltip,
    IconButton,
    TablePagination,
    Grid,
    Card,
    CardContent,
    CardActions,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
    Sync,
    Delete,
    FilterList
} from '@mui/icons-material';

import FilterSection from './FilterSection';
// ... other imports remain the same


import { alpha } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import get from 'lodash/get';
import set from 'lodash/set';
import FormInput from '../form/form-input';
import useForm from '../hooks/useForm';
import http from '../utils/http';

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const DEFAULT_ORDER = 'asc';
const DEFAULT_ORDER_BY = 'calories';
const DEFAULT_ROWS_PER_PAGE = 5;

function EnhancedTableHead(props) {
    const {
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        onRequestSort,
    } = props;
    const createSortHandler = (newOrderBy) => (event) => {
        onRequestSort(event, newOrderBy);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all desserts',
                        }}
                    />
                </TableCell>
                {props.headCells.map((headCell, index) => (
                    <TableCell
                        key={index}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

function EnhancedTableToolbar(props) {
    const {
        numSelected,
        selectedRows,
        tableName,
        state,
        onDelete,
        onChange,
        onRefresh,
    } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                    <Typography
                        sx={{ flex: '1 1 100%' }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >
                        {tableName}
                    </Typography>
                )}

            {numSelected > 0 ? (
                <Tooltip title="Delete">
                    <IconButton onClick={() => onDelete({ selectedRows })}>
                        <Delete />
                    </IconButton>
                </Tooltip>
            ) : (
                    <Tooltip title="Filter list" onClick={() => onChange(!state.values.showFilter, 'showFilter')}>
                        <IconButton>
                            <FilterList />
                        </IconButton>
                    </Tooltip>
                )}
            <Tooltip title="Refresh" onClick={onRefresh}>
                <IconButton>
                    <Sync />
                </IconButton>
            </Tooltip>
        </Toolbar>
    );
}

const EnhancedTable = React.forwardRef((props, ref) => {
    const [order, setOrder] = React.useState(DEFAULT_ORDER);
    const [orderBy, setOrderBy] = React.useState(DEFAULT_ORDER_BY);

    const theme = useTheme();
    const isTileView = useMediaQuery(theme.breakpoints.down('md'));

    let {
        filterList,
        actionRowClick,
        actionDelete,
        tableName,
        headCells,
        searchEndpont,
        searchAdditionalQueryParams,
        pageSize
    } = props;

    let initialState = {
        headCells: headCells,
        rows: [],
        page: 0,
        totalCount: 0,
        pageSize: pageSize || DEFAULT_ROWS_PER_PAGE,
        visibleRows: [],
        searchEndpont,
        dense: false,
        showFilter: false,
        paddingHeight: 0,
        searchAdditionalQueryParams,
        selectedRows: [],
    };

    if (filterList) {
        let filterValues = {};
        filterList.map(x => {
            filterValues = {
                ...filterValues,
                [x.id]: x.defaultValue
            };
        });
        initialState = {
            ...initialState,
            filter: filterValues
        };
    }

    const {
        state,
        onChange,
        onChangeBulk,
        onRefresh,
    } = useForm({
        initialState,
        onLoad,
        stateToTriggerOnLoad: [
            'page',
            'pageSize',
            ...Object.keys(initialState.filter).map(x => `filter.${x}`)
        ],
        onSubmit,
        props
    });

    useImperativeHandle(ref, () => ({
        onRefresh
    }));


    const handleRequestSort =
        (_, newOrderBy) => {
            const isAsc = orderBy === newOrderBy && order === 'asc';
            const toggledOrder = isAsc ? 'desc' : 'asc';
            setOrder(toggledOrder);
            setOrderBy(newOrderBy);

            const sortedRows = stableSort(state.values.rows, getComparator(toggledOrder, newOrderBy));
            const updatedRows = sortedRows.slice(
                state.values.page * state.values.pageSize,
                state.values.page * state.values.pageSize + state.values.pageSize,
            );

            onChange(updatedRows, 'visibleRows');
        };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = state.values.rows.map((n) => n._id);
            onChange(newSelected, 'selectedRows');
            return;
        }
        onChange([], 'selectedRows');
    };

    const handleClick = (_, row) => {
        const selectedIndex = state.values.selectedRows.indexOf(row._id);
        actionRowClick({ index: selectedIndex, rowData: row });
    };

    const handleCheck = (_, row) => {
        const selectedIndex = state.values.selectedRows.indexOf(row._id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(state.values.selectedRows, row._id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(state.values.selectedRows.slice(1));
        } else if (selectedIndex === state.values.selectedRows.length - 1) {
            newSelected = newSelected.concat(state.values.selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                state.values.selectedRows.slice(0, selectedIndex),
                state.values.selectedRows.slice(selectedIndex + 1),
            );
        }

        onChange(newSelected, 'selectedRows');
    };

    const handleChangePage =
        (_, newPage) => {
            onChange(newPage, 'page');
        };

    const handleChangeRowsPerPage =
        (event) => {
            const updatedRowsPerPage = parseInt(event.target.value, 10);
            onChangeBulk({
                pageSize: updatedRowsPerPage,
                page: 0,
            });
            onChange(0, 'paddingHeight');
        };

    const isSelected = (_id) => state.values.selectedRows.indexOf(_id) !== -1;

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <EnhancedTableToolbar
                    tableName={tableName}
                    numSelected={state.values.selectedRows.length}
                    selectedRows={state.values.selectedRows}
                    state={state}
                    onChange={onChange}
                    filterList={props.filterList}
                    onDelete={actionDelete}
                    onRefresh={onRefresh}
                />

                {/* Updated Filter Section */}
                {state.values.showFilter && (
                    <FilterSection
                        filterList={props.filterList}
                        state={state}
                        onChangeBulk={onChangeBulk}
                    />
                )}

                {/* Conditional Rendering based on viewport size */}
                {isTileView ? (
                    <TileView
                        rows={state.values.visibleRows}
                        headCells={props.headCells}
                        handleClick={handleClick}
                        handleCheck={handleCheck}
                        isSelected={isSelected}
                        numSelected={state.values.selectedRows.length}
                        onDelete={actionDelete}
                    />
                ) : (
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={state.values.dense ? 'small' : 'medium'}
                        >
                            <EnhancedTableHead
                                numSelected={state.values.selectedRows.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                onDelete={actionDelete}
                                rowCount={state.values.rows.length}
                                headCells={state.values.headCells}
                            />
                            <TableBody>
                                {state.values.visibleRows
                                    ? state.values.visibleRows.map((row, index) => {
                                        const isItemSelected = isSelected(row._id);
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow
                                                hover
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={index}
                                                selected={isItemSelected}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        color="primary"
                                                        onClick={(event) => handleCheck(event, row)}
                                                        checked={isItemSelected}
                                                        inputProps={{
                                                            'aria-labelledby': labelId,
                                                        }}
                                                    />
                                                </TableCell>
                                                {state.values.headCells.map((headCell, index) => {
                                                    if (headCell.render)
                                                        return headCell.render({
                                                            rowData: row,
                                                            handleClick,
                                                            index
                                                        });
                                                    return (
                                                        <TableCell
                                                            onClick={(event) => handleClick(event, row)}
                                                            component="th"
                                                            id={labelId}
                                                            key={index}
                                                            scope="row"
                                                            padding="none">
                                                            {!isEmpty(get(row, headCell.id)) && get(row, headCell.id).toString()}
                                                        </TableCell>

                                                    );

                                                })}
                                            </TableRow>
                                        );
                                    })
                                    : null}
                                {state.values.paddingHeight > 0 && (
                                    <TableRow
                                        style={{
                                            height: state.values.paddingHeight,
                                        }}
                                    >
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination remains the same for both views */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={state.values.totalCount}
                    rowsPerPage={state.values.pageSize}
                    page={state.values.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
});

export default EnhancedTable;

EnhancedTable.propTypes = {
    tableName: PropTypes.string,
    searchEndpont: PropTypes.string,
    searchAdditionalQueryParams: PropTypes.any,
    actionRowClick: PropTypes.func,
    actionDelete: PropTypes.func,
    filterList: PropTypes.arrayOf(PropTypes.exact({
        id: PropTypes.string,
        dataType: PropTypes.string,
        label: PropTypes.string,
        defaultValue: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.bool,

        ]),
    })),
    headCells: PropTypes.arrayOf(PropTypes.exact({
        id: PropTypes.string,
        numeric: PropTypes.bool,
        disablePadding: PropTypes.bool,
        label: PropTypes.string,
        render: PropTypes.func,
    }))
};

const getRecords = async (values) => {
    const { page, pageSize, filter, searchAdditionalQueryParams } = values;

    // Filter out empty filter values
    const filteredParams = Object.keys(filter).reduce((acc, key) => {
        if (filter[key] !== '') acc[key] = filter[key];
        return acc;
    }, {});

    const rows = await http.get(
        {
            pageOffset: page,
            pageSize,
            ...filteredParams,
            ...searchAdditionalQueryParams
        },
        values.searchEndpont
    );

    return {
        loading: false,
        rows: rows.rows,
        totalCount: rows.totalCount
    };
};

const onLoad = async ({ values }) => {
    try {
        const result = await getRecords(values);
        if (result.errored) {
            return {
                ...result,
                isValid: false,
            };
        }
        let rowsOnMount = stableSort(
            result.rows,
            getComparator(DEFAULT_ORDER, DEFAULT_ORDER_BY),
        );

        rowsOnMount = rowsOnMount.slice(
            0 * values.pageSize,
            0 * values.pageSize + values.pageSize,
        );

        const numEmptyRows =
            values.page > 0 ?
                Math.max(0, values.pageSize - result.rows.length) : 0;
        const newPaddingHeight = (values.dense ? 24 : 42) * numEmptyRows;
        return {
            ...values,
            ...result,
            visibleRows: rowsOnMount,
            isValid: true,
            paddingHeight: newPaddingHeight
        };
    } catch (e) {
        console.log(e);
        return;
    }
};

const onSubmit = async ({ values, onInvalidForm, onFail, onSuccess }) => {
    try {
        const errors = {};
        if (!isEmpty(errors)) return onInvalidForm(errors);

        const result = await getRecords(values);
        if (result.errored) return onFail(result.error);

        onSuccess({
            saved: true,
            rows: result.rows,
            totalCount: result.totalCount,
        });
    } catch (e) {
        return onFail(e.message);
    }
};


function TileView({
    rows,
    headCells,
    handleClick,
    handleCheck,
    isSelected,
    numSelected,
    onDelete,
}) {
    return (
        <Grid container spacing={2} padding={2}>
            {rows.map((row, index) => {
                const selected = isSelected(row._id);
                return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                            variant="outlined"
                            sx={{
                                borderColor: selected ? 'primary.main' : 'grey.300',
                                cursor: 'pointer',
                            }}
                            onClick={(event) => handleClick(event, row)}
                        >
                            <CardContent>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={selected}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleCheck(event, row);
                                            }}
                                        />
                                    }
                                    label=""
                                />
                                {headCells.map((headCell, idx) => (
                                    <Typography key={idx} variant="body2">
                                        <strong>{headCell.label}:</strong> {get(row, headCell.id)?.toString() || ''}
                                    </Typography>
                                ))}
                            </CardContent>
                            <CardActions>
                                {/* Add any row-specific actions here if needed */}
                            </CardActions>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
}

TileView.propTypes = {
    rows: PropTypes.array.isRequired,
    headCells: PropTypes.array.isRequired,
    handleClick: PropTypes.func.isRequired,
    handleCheck: PropTypes.func.isRequired,
    isSelected: PropTypes.func.isRequired,
    numSelected: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired,
};
